import { useState } from "react";
import { toN, mkMap, makeId, normalizeKey } from "../utils";
import { Card, ST, NI, BulkBtn, PastePanel } from "../ui/SharedUI";
import { calcMonthlySchedule } from "./LoanLedger";

export default function ExpenseEditor({expenses, setExpenses, expenseItems, setExpenseItems, expOverride, setExpOverride, staff, months, expenseMemos, setExpenseMemos, loans}) {
  const [pasteConfirm, setPasteConfirm] = useState(null);
  const [editLabelId,  setEditLabelId]  = useState(null);
  const [showMemos, setShowMemos] = useState(false);

  const autoVals = {
    salary:    Object.fromEntries(months.map(m=>[m, staff.reduce((s,p)=>s+toN(p.salary[m]),0)])),
    bonus:     Object.fromEntries(months.map(m=>[m, staff.reduce((s,p)=>s+toN(p.bonus[m]),0)])),
    transport: Object.fromEntries(months.map(m=>[m, staff.reduce((s,p)=>s+toN(p.transport[m]),0)])),
    insurance: Object.fromEntries(months.map(m => {
      const salaryTotal = staff.reduce((s,p)=>s+toN(p.salary[m]),0);
      const bonusTotal = staff.reduce((s,p)=>s+toN(p.bonus[m]),0);
      const officerTotal = toN(expenses["officer"]?.[m]);
      return [m, Math.round((salaryTotal + bonusTotal + officerTotal) * 0.15)];
    })),
    loanInterest: Object.fromEntries(months.map(m => {
      const total = (loans || []).reduce((sum, loan) => {
        const s = calcMonthlySchedule(loan, months);
        return sum + (s[m]?.interest ?? 0);
      }, 0);
      return [m, total];
    })),
  };

  const getV = (id,m) => {
    const item = expenseItems.find(e=>e.id===id);
    if (item?.auto && !expOverride[id]) return toN(autoVals[item.autoKey]?.[m]);
    return toN(expenses[id]?.[m]);
  };
  const upd    = (id,m,val) => setExpenses(prev=>({...prev,[id]:{...prev[id],[m]:val}}));
  const setAll = (id,val)   => setExpenses(prev=>({...prev,[id]:mkMap(months,toN(val))}));
  const getMemo = (id, m) => expenseMemos?.[id]?.[m] ?? "";
  const hasMemo = (id, m) => getMemo(id, m).trim() !== "";
  const updMemo = (id, m, val) => setExpenseMemos(prev => ({...prev, [id]: {...(prev?.[id] || {}), [m]: val}}));
  const updLabel = (id, label) => setExpenseItems(prev=>prev.map(e=>e.id===id?{...e,label}:e));
  const addItem = () => {
    const id = makeId("custom");
    setExpenseItems(prev=>[...prev, {id, label:"新規科目", auto:false}]);
    setExpenses(prev=>({...prev, [id]: mkMap(months, 0)}));
    setEditLabelId(id);
  };
  const removeItem = (id) => {
    const item = expenseItems.find(e=>e.id===id);
    if (item?.auto) { alert(`「${item.label}」は自動連携科目のため削除できません。`); return; }
    if (!window.confirm(`「${item?.label}」を削除しますか？`)) return;
    setExpenseItems(prev=>prev.filter(e=>e.id!==id));
    setExpenses(prev=>{ const n={...prev}; delete n[id]; return n; });
  };
  const applyExpPaste = (preview) => {
    const newItems = [...expenseItems];
    const newExp   = {...expenses};
    const autoMatchIds = [];
    preview.rows.forEach((r, i) => {
      const pastedLabel = (r.label ?? r.name ?? "").trim();
      let matched = null;
      if (pastedLabel) { matched = newItems.find(e => normalizeKey(e.label) === normalizeKey(pastedLabel)) || null; }
      if (!matched && !pastedLabel && i < newItems.length) matched = newItems[i];
      if (matched) {
        if (matched.auto && !expOverride[matched.id]) autoMatchIds.push(matched.id);
        newExp[matched.id] = Object.fromEntries(months.map((m,j)=>[m, String(r.values[j] ?? 0)]));
      } else if (pastedLabel) {
        const id = makeId("custom");
        newItems.push({id, label:pastedLabel, auto:false});
        newExp[id] = Object.fromEntries(months.map((m,j)=>[m, String(r.values[j] ?? 0)]));
      }
    });
    const uniqueAutoIds = [...new Set(autoMatchIds)];
    if (uniqueAutoIds.length > 0) { setPasteConfirm({autoIds: uniqueAutoIds, newItems, newExp}); }
    else { setExpenseItems(newItems); setExpenses(newExp); }
  };
  const doApplyExpPaste = (newItems, newExp, forceManualIds) => {
    const newOverride = {...expOverride};
    forceManualIds.forEach(id => { newOverride[id] = true; });
    setExpOverride(newOverride); setExpenseItems(newItems); setExpenses(newExp); setPasteConfirm(null);
  };

  const insuranceItem = expenseItems.find(e => e.id === "insurance");
  const insuranceIsAuto = insuranceItem?.auto && !expOverride["insurance"];

  return (
    <div style={{display:"flex",flexDirection:"column",gap:"16px"}}>
      {pasteConfirm && (
        <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.4)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center"}}>
          <div style={{background:"#fff",borderRadius:"10px",padding:"24px",maxWidth:"420px",width:"90%",boxShadow:"0 8px 32px rgba(0,0,0,0.2)"}}>
            <div style={{fontSize:"15px",fontWeight:"700",color:"#1A3A1A",marginBottom:"12px",fontFamily:"'Noto Sans JP',sans-serif"}}>⚠️ 自動モードの科目を上書き</div>
            <p style={{fontSize:"12px",color:"#555",lineHeight:"1.7",fontFamily:"'Noto Sans JP',sans-serif",marginBottom:"16px"}}>以下の科目は現在「自動」モードです。貼り付けると「手動」に切り替わります。</p>
            <div style={{background:"#FFF8E1",border:"1px solid #FFD54F",borderRadius:"6px",padding:"10px 14px",marginBottom:"16px"}}>
              {pasteConfirm.autoIds.map(id => (
                <div key={id} style={{fontSize:"12px",color:"#AA5500",fontFamily:"'Noto Sans JP',sans-serif"}}>・{expenseItems.find(e=>e.id===id)?.label}</div>
              ))}
            </div>
            <div style={{display:"flex",gap:"10px",justifyContent:"flex-end"}}>
              <button onClick={()=>setPasteConfirm(null)} style={{padding:"8px 16px",background:"#F0F4F0",border:"1px solid #D0D8D0",borderRadius:"6px",cursor:"pointer",fontSize:"12px",fontFamily:"'Noto Sans JP',sans-serif",color:"#555"}}>キャンセル</button>
              <button onClick={()=>doApplyExpPaste(pasteConfirm.newItems, pasteConfirm.newExp, pasteConfirm.autoIds)} style={{padding:"8px 16px",background:"#AA5500",color:"#fff",border:"none",borderRadius:"6px",cursor:"pointer",fontSize:"12px",fontFamily:"'Noto Sans JP',sans-serif",fontWeight:"700"}}>手動に切替えて貼り付け</button>
            </div>
          </div>
        </div>
      )}

      <Card>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"8px"}}>
          <ST>経費設定</ST>
          <button onClick={()=>setShowMemos(v=>!v)} style={{padding:"5px 10px",fontSize:"11px",border:"1px solid #D0D8D0",borderRadius:"6px",background:showMemos?"#F0F4F0":"#fff",cursor:"pointer",fontFamily:"'Noto Sans JP',sans-serif"}}>📝 メモ表示</button>
        </div>
        <p style={{fontSize:"11px",color:"#888",marginBottom:"16px",fontFamily:"'Noto Sans JP',sans-serif"}}>給料・交通費・賞与は各タブの設定が自動反映（緑）されます。「手動」ボタンで直接入力に切り替えられます。科目名はクリックで編集できます。</p>
        <PastePanel mode="expense_row" onApply={applyExpPaste} months={months}/>
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:"12px"}}>
            <thead><tr style={{borderBottom:"2px solid #E8EDE8"}}>
              <th style={{textAlign:"left",padding:"5px 8px",color:"#888",minWidth:"110px"}}>科目</th>
              <th style={{textAlign:"center",padding:"5px 5px",color:"#888",width:"48px"}}>モード</th>
              <th style={{textAlign:"center",padding:"5px 4px",color:"#888",width:"44px"}}>一括</th>
              {months.map(m=><th key={m} style={{textAlign:"right",padding:"5px 2px",color:"#888",minWidth:"66px"}}>{m}</th>)}
              <th style={{textAlign:"right",padding:"5px 8px",color:"#888",minWidth:"72px",position:"sticky",right:0,background:"#fff",boxShadow:"-2px 0 4px rgba(0,0,0,0.06)"}}>合計</th>
              <th style={{textAlign:"center",padding:"5px 4px",color:"#888",width:"28px"}}></th>
            </tr></thead>
            <tbody>
              {expenseItems.map((item,ii)=>{
                if (item.id === "insurance") return null;
                const isAuto = item.auto && !expOverride[item.id];
                return (
                  <tr key={item.id} style={{borderBottom:"1px solid #F0F4F0",background:ii%2===0?"#FAFCFA":"#fff"}}>
                    <td style={{padding:"4px 8px"}}>
                      {editLabelId===item.id
                        ? <input autoFocus value={item.label} onChange={e=>updLabel(item.id, e.target.value)} onBlur={()=>setEditLabelId(null)} onKeyDown={e=>e.key==="Enter"&&setEditLabelId(null)} style={{width:"90px",border:"1px solid #C0392B",borderRadius:"3px",padding:"2px 4px",fontSize:"12px",fontFamily:"'Noto Sans JP',sans-serif"}}/>
                        : <span onClick={()=>setEditLabelId(item.id)} style={{cursor:"pointer",color:"#444",fontFamily:"'Noto Sans JP',sans-serif",borderBottom:"1px dotted #ccc",paddingBottom:"1px"}} title="クリックで科目名を編集">{item.label}</span>
                      }
                    </td>
                    <td style={{padding:"4px 4px",textAlign:"center"}}>
                      {item.auto
                        ? <button onClick={()=>setExpOverride(prev=>({...prev,[item.id]:!prev[item.id]}))} style={{fontSize:"10px",padding:"2px 7px",background:isAuto?"#E8F4E8":"#FFF3E0",color:isAuto?"#2D7A2D":"#AA5500",border:`1px solid ${isAuto?"#C8E6C8":"#FFCC80"}`,borderRadius:"3px",cursor:"pointer",whiteSpace:"nowrap"}}>{isAuto?"自動":"手動"}</button>
                        : <span style={{fontSize:"10px",color:"#bbb"}}>—</span>
                      }
                    </td>
                    <td style={{padding:"4px 4px",textAlign:"center"}}>
                      {!isAuto ? <BulkBtn onSet={val=>setAll(item.id,val)}/> : <span style={{fontSize:"10px",color:"#bbb"}}>—</span>}
                    </td>
                    {months.map(m=>(
                      <td key={m} style={{padding:"3px 1px",textAlign:"right",position:"relative",verticalAlign:"top"}}>
                        {hasMemo(item.id, m) && !showMemos && <span style={{position:"absolute",top:"0px",right:"4px",fontSize:"9px",color:"#666"}}>●</span>}
                        {isAuto
                          ? <span style={{display:"inline-block",width:"64px",padding:"4px 3px",textAlign:"right",fontSize:"12px",fontFamily:"monospace",color:"#2D7A2D",background:"#F0FAF0",borderRadius:"4px"}}>{toN(autoVals[item.autoKey]?.[m]).toLocaleString()}</span>
                          : <NI value={getV(item.id,m)} onChange={val=>upd(item.id,m,val)} w="64px"/>
                        }
                        {showMemos && (
                          <input type="text" value={getMemo(item.id, m)} onChange={e=>updMemo(item.id, m, e.target.value)} style={{marginTop:"3px",width:"64px",boxSizing:"border-box",padding:"2px 4px",fontSize:"10px",border:"1px solid #E0E0E0",borderRadius:"4px",background:"#F5F5F5"}} />
                        )}
                      </td>
                    ))}
                    <td style={{padding:"4px 8px",textAlign:"right",fontFamily:"monospace",fontWeight:"700",color:isAuto?"#2D7A2D":"#1A3A1A",position:"sticky",right:0,background:ii%2===0?"#FAFCFA":"#fff",boxShadow:"-2px 0 4px rgba(0,0,0,0.06)"}}>{months.reduce((s,m)=>s+getV(item.id,m),0).toLocaleString()}</td>
                    <td style={{padding:"4px 4px",textAlign:"center"}}>
                      {item.auto
                        ? <span title="自動連携科目は削除できません" style={{fontSize:"11px",color:"#e0e0e0",padding:"2px 4px",lineHeight:1,cursor:"not-allowed"}}>✕</span>
                        : <button onClick={()=>removeItem(item.id)} title="この科目を削除" style={{fontSize:"11px",color:"#ccc",background:"none",border:"none",cursor:"pointer",padding:"2px 4px",borderRadius:"3px",lineHeight:1}} onMouseEnter={e=>e.target.style.color="#C0392B"} onMouseLeave={e=>e.target.style.color="#ccc"}>✕</button>
                      }
                    </td>
                  </tr>
                );
              })}
              <tr style={{background:"#F8FBF8"}}>
                <td colSpan={3+months.length+2} style={{padding:"6px 8px"}}>
                  <button onClick={addItem} style={{fontSize:"11px",color:"#2D7A2D",background:"none",border:"1px dashed #C8E6C8",borderRadius:"4px",cursor:"pointer",padding:"3px 12px",fontFamily:"'Noto Sans JP',sans-serif"}}>＋ 科目を追加</button>
                </td>
              </tr>
              <tr style={{borderTop:"2px solid #E8EDE8",background:"#FFF5F5"}}>
                <td colSpan={3} style={{padding:"6px 8px",fontWeight:"700",color:"#C0392B",fontFamily:"'Noto Sans JP',sans-serif",fontSize:"12px"}}>月次合計</td>
                {months.map(m=><td key={m} style={{padding:"6px 2px",textAlign:"right",fontFamily:"monospace",fontWeight:"700",color:"#C0392B",fontSize:"12px"}}>{expenseItems.filter(item=>item.id!=="insurance").reduce((s,item)=>s+getV(item.id,m),0).toLocaleString()}</td>)}
                <td style={{padding:"6px 8px",textAlign:"right",fontFamily:"monospace",fontWeight:"800",color:"#C0392B",fontSize:"13px",position:"sticky",right:0,background:"#FFF5F5",boxShadow:"-2px 0 4px rgba(0,0,0,0.06)"}}>{expenseItems.filter(item=>item.id!=="insurance").reduce((s,item)=>s+months.reduce((ss,m)=>ss+getV(item.id,m),0),0).toLocaleString()}</td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>

      {insuranceItem && (
        <Card>
          <div style={{display:'flex', alignItems:'center', gap:'10px', marginBottom:'8px'}}>
            <ST>社会保険料</ST>
            <span style={{fontSize:'10px', padding:'2px 8px', borderRadius:'999px', background:'#E8F4E8', color:'#2D7A2D', fontWeight:'700', fontFamily:"'Noto Sans JP',sans-serif"}}>
              自動計算（給与＋賞与＋役員報酬 × 15%）
            </span>
            <button
              onClick={() => setExpOverride(prev => ({...prev, insurance: !prev['insurance']}))}
              style={{fontSize:'10px', padding:'2px 7px', background:insuranceIsAuto?'#E8F4E8':'#FFF3E0', color:insuranceIsAuto?'#2D7A2D':'#AA5500', border:`1px solid ${insuranceIsAuto?'#C8E6C8':'#FFCC80'}`, borderRadius:'3px', cursor:'pointer', whiteSpace:'nowrap'}}
            >
              {insuranceIsAuto ? '自動' : '手動'}
            </button>
          </div>
          <p style={{fontSize:'11px', color:'#888', marginBottom:'12px', fontFamily:"'Noto Sans JP',sans-serif"}}>
            給与・賞与・役員報酬の合計に対する会社負担分の概算（15%）です。「手動」ボタンで直接入力に切り替えられます。
          </p>
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%', borderCollapse:'collapse', fontSize:'12px'}}>
              <thead>
                <tr style={{borderBottom:'2px solid #E8EDE8'}}>
                  <th style={{textAlign:'left', padding:'5px 8px', color:'#888', minWidth:'110px'}}>科目</th>
                  <th style={{textAlign:'center', padding:'5px 5px', color:'#888', width:'48px'}}>モード</th>
                  <th style={{textAlign:'center', padding:'5px 4px', color:'#888', width:'44px'}}>一括</th>
                  {months.map(m => <th key={m} style={{textAlign:'right', padding:'5px 2px', color:'#888', minWidth:'66px'}}>{m}</th>)}
                  <th style={{textAlign:'right', padding:'5px 8px', color:'#888', minWidth:'72px', position:'sticky', right:0, background:'#fff', boxShadow:'-2px 0 4px rgba(0,0,0,0.06)'}}>合計</th>
                  <th style={{width:'28px'}}></th>
                </tr>
              </thead>
              <tbody>
                <tr style={{borderBottom:'1px solid #F0F4F0', background:'#FAFCFA'}}>
                  <td style={{padding:'4px 8px'}}>
                    <span style={{color:'#444', fontFamily:"'Noto Sans JP',sans-serif"}}>{insuranceItem.label}</span>
                  </td>
                  <td style={{padding:'4px 4px', textAlign:'center'}}>
                    <button
                      onClick={() => setExpOverride(prev => ({...prev, insurance: !prev['insurance']}))}
                      style={{fontSize:'10px', padding:'2px 7px', background:insuranceIsAuto?'#E8F4E8':'#FFF3E0', color:insuranceIsAuto?'#2D7A2D':'#AA5500', border:`1px solid ${insuranceIsAuto?'#C8E6C8':'#FFCC80'}`, borderRadius:'3px', cursor:'pointer', whiteSpace:'nowrap'}}
                    >
                      {insuranceIsAuto ? '自動' : '手動'}
                    </button>
                  </td>
                  <td style={{padding:'4px 4px', textAlign:'center'}}>
                    {!insuranceIsAuto ? <BulkBtn onSet={val => setAll('insurance', val)}/> : <span style={{fontSize:'10px', color:'#bbb'}}>—</span>}
                  </td>
                  {months.map(m => (
                    <td key={m} style={{padding:'3px 1px', textAlign:'right'}}>
                      {insuranceIsAuto
                        ? <span style={{display:'inline-block', width:'64px', padding:'4px 3px', textAlign:'right', fontSize:'12px', fontFamily:'monospace', color:'#2D7A2D', background:'#F0FAF0', borderRadius:'4px'}}>{toN(autoVals['insurance']?.[m]).toLocaleString()}</span>
                        : <NI value={getV('insurance', m)} onChange={val => upd('insurance', m, val)} w="64px"/>
                      }
                    </td>
                  ))}
                  <td style={{padding:'4px 8px', textAlign:'right', fontFamily:'monospace', fontWeight:'700', color:insuranceIsAuto?'#2D7A2D':'#1A3A1A', position:'sticky', right:0, background:'#FAFCFA', boxShadow:'-2px 0 4px rgba(0,0,0,0.06)'}}>
                    {months.reduce((s, m) => s + getV('insurance', m), 0).toLocaleString()}
                  </td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
